import type { VisibilityPayloadType } from '@unifyapps/defs/types/block';
import {
  type CopilotChatIntegrationConfigType,
  IntegrationApps,
} from '@unifyapps/defs/types/interface';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import { useMemo } from 'react';
import { EMPTY_ARRAY } from '../../../../../consts/empty';
import { DependencyHelper } from '../../../../../dependency/helpers/DependencyHelper';
import { DeltaMatrixUAMethods as UAMethods } from '../../../../const/DeltaMatrixUAMethods';
import noCodeBuildDependencyGraph from '../../../../dependency/noCodeBuildDependencyGraph';
import { useReactiveComputeContext } from '../../../../hooks/computeContext';
import {
  type EvaluateConditionalOperator,
  useEvaluateConditionalOperator,
} from '../../../../hooks/useComputeConditionEvaluationGetter';
import useDynamicEffect from '../../../../hooks/useDynamicEffect';
import { useInterfaceStore } from '../../../../stores/InterfaceStore';
import { isNoCodeEntityId } from '../../../../utils/isNoCodeEntityId';

const evaluateVisibility = (
  context: Record<string, unknown>,
  evaluateConditionalOperator: EvaluateConditionalOperator,
  visibility?: VisibilityPayloadType,
) => {
  if (!visibility) return true;

  if (visibility.value === 'conditions') {
    const conditions = evaluateConditionalOperator({
      conditionEvaluationObj: visibility.conditions,
      context,
    });

    return conditions !== false;
  }

  return visibility.value;
};

export const useIntegrationsVisibility = () => {
  const interfaceRecord = useInterfaceStore().hooks.record.useDetails();
  const { context } = useReactiveComputeContext();
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();

  const integrations = interfaceRecord.properties.integrations;

  const integrationsDeps = useMemo(() => {
    if (!integrations) return EMPTY_ARRAY;

    const integrationsDependencyGraph = noCodeBuildDependencyGraph({
      objects: integrations,
      isValidEntity: isNoCodeEntityId,
    });

    return Object.values(integrationsDependencyGraph).reduce<unknown[]>((acc, item) => {
      const contextPaths =
        DependencyHelper.getDependsOn(item)?.reduce<string[]>((acc, d) => {
          acc.push(...DependencyHelper.getDependsOnProperties(d));
          return acc;
        }, []) ?? [];

      acc.push(...contextPaths.map((it) => _get(context, it)));
      return acc;
    }, []);
  }, [context, integrations]);

  useDynamicEffect(() => {
    _forEach(integrations, (integration, key) => {
      switch (key) {
        case IntegrationApps.CopilotChat: {
          const { enabled, visibility } = integration as CopilotChatIntegrationConfigType;

          if (enabled && typeof window !== 'undefined' && 'UnifyApps' in window) {
            const isVisible = evaluateVisibility(context, evaluateConditionalOperator, visibility);

            if (isVisible) {
              (window.UnifyApps as (method: UAMethods) => Promise<void>)(
                UAMethods.showCopilotTrigger,
              );
            } else {
              (window.UnifyApps as (method: UAMethods) => Promise<void>)(
                UAMethods.hideCopilotTrigger,
              );
            }
          }

          break;
        }
        default:
          break;
      }
    });
  }, integrationsDeps);
};
